import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Space,
} from "antd";
import { handleApiError } from "utils";
import { ticketUsersService } from "services/tickets";
import { timeTrackerAssignableUserLabel } from "utils/time_tracker_assignable_user_label";
import { PROJECT_COLORS } from "../constants";

const { TextArea } = Input;

export default function TicketProjectForm({ form, onFinish, submitLabel, loading }) {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    setUsersLoading(true);
    ticketUsersService()
      .then((res) => setUsers(res.data.items || []))
      .catch(handleApiError)
      .finally(() => setUsersLoading(false));
  }, []);

  const userOptions = useMemo(
    () =>
      [...users]
        .map((u) => ({
          value: u._id,
          label: timeTrackerAssignableUserLabel(u),
        }))
        .sort((a, b) =>
          (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" })
        ),
    [users]
  );

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Row gutter={16}>
        <Col xs={24} md={16}>
          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true, message: "Project name is required" }]}
          >
            <Input placeholder="e.g. Website Redesign, Q2 Launch" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="prefix" label="Ticket Prefix">
            <Input placeholder="TKT" maxLength={6} style={{ textTransform: "uppercase" }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="description" label="Description">
        <TextArea rows={3} placeholder="What is this project about?" />
      </Form.Item>

      <Form.Item name="color" label="Color">
        <Select placeholder="Choose a color">
          {PROJECT_COLORS.map((c) => (
            <Select.Option key={c} value={c}>
              <span
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: c,
                  marginRight: 8,
                  verticalAlign: "middle",
                }}
              />
              {c}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="members"
        label="Team Members"
        extra="Includes everyone under this reseller’s users list plus Super Admin / MSP staff (roles 10–13) who may not share the same client."
      >
        <Select
          mode="multiple"
          placeholder="Assign team members"
          loading={usersLoading}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          dropdownRender={(menu) => (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  borderBottom: "1px solid #f0f0f0",
                  gap: 12,
                }}
              >
                <span
                  style={{ cursor: "pointer", color: "#1890ff" }}
                  onClick={() =>
                    form.setFieldsValue({ members: users.map((u) => u._id) })
                  }
                >
                  Select all users
                </span>
                <span
                  style={{ cursor: "pointer", color: "#1890ff" }}
                  onClick={() => form.setFieldsValue({ members: [] })}
                >
                  Clear all
                </span>
              </div>
              {menu}
            </>
          )}
          options={userOptions}
        />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {submitLabel || "Save Project"}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
